'use client'

import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import type { ComponentType } from 'react'
import type { UpsellProduct, UpsellGroup } from '@sifly/shared-types'
import { cropSquareUrl } from '@sifly/shared-utils'
import { CardPrice, SaleBadge } from './CardPrice'

/**
 * A "choose one" kit card: the customer picks which member (wing set, drive,
 * …) then adds it. One card stands in for several products so the kit row
 * stays uncluttered. Renders the selected member's image + price; the selector
 * is a plain styled <select> (same affordance as the bundle configurator).
 *
 * Client component — it owns the selected-option state. The Add action reuses
 * the injected storefront `AddButton` with the selected member's payload, so
 * adding behaves identically to every other kit add (opens the cart drawer).
 * If a member can't be one-click added (no Stripe / no buyable SKU) the card
 * falls back to a "View product →" link to that member's page.
 */
export function UpsellGroupCard({
  group,
  AddButton,
}: {
  group: UpsellGroup
  AddButton?: ComponentType<{ payload: NonNullable<UpsellProduct['addToCart']> }>
}) {
  const [idx, setIdx] = useState(0)
  const selected = group.options[idx] ?? group.options[0]
  if (!selected) return null

  const cardImage = cropSquareUrl(selected.photos[0])

  return (
    <div className="shrink-0 w-48 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-primary)] overflow-hidden">
      <div className="relative aspect-square bg-[var(--color-bg-secondary)]">
        {cardImage && (
          <Image src={cardImage} alt={selected.name} fill sizes="192px" className="object-cover" />
        )}
        {selected.onSale && <SaleBadge className="absolute left-2 top-2" />}
      </div>
      <div className="p-3">
        <h3
          className="text-xs font-medium text-[var(--color-text-primary)] line-clamp-2"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {group.label}
        </h3>
        <select
          aria-label={`Choose ${group.label}`}
          value={idx}
          onChange={(e) => setIdx(Number(e.target.value))}
          className="mt-1 w-full text-xs rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] px-2 py-1"
        >
          {group.options.map((opt, i) => (
            <option key={opt.id} value={i}>
              {opt.name}
            </option>
          ))}
        </select>
        <CardPrice
          displayPrice={selected.displayPrice}
          compareAtPrice={selected.compareAtPrice}
          priceFromMultiple={selected.priceFromMultiple}
          onSale={selected.onSale}
          className="mt-1 text-xs text-[var(--color-text-secondary)]"
        />
        {selected.upsellNote && (
          <p className="mt-1 text-[11px] leading-snug text-[var(--color-text-tertiary)]">{selected.upsellNote}</p>
        )}
        {selected.addToCart && AddButton ? (
          <AddButton payload={selected.addToCart} />
        ) : (
          <Link
            href={`/products/${selected.slug ?? selected.id}`}
            className="mt-2 inline-block text-xs font-medium text-[var(--color-link)] hover:text-[var(--color-link-hover)] transition-colors"
          >
            View product →
          </Link>
        )}
      </div>
    </div>
  )
}
